export type OrderLike = Record<string, any>;
export type TradeLike = Record<string, any>;

type Lot = {
  quantity: number;
  costPerUnit: number;
};

const finite = (value: unknown, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

function fallbackFunds(order: OrderLike, quantity: number) {
  const price = finite(order.price);
  if (quantity <= 0 || price <= 0) return 0;
  if (order.side === "bid" && order.ord_type === "price") return price;
  return price * quantity;
}

export function summarizeOrders(orders: OrderLike[], trades: TradeLike[]): OrderLike[] {
  const tradesByOrder = new Map<string, TradeLike[]>();
  for (const trade of trades) {
    const orderUuid = String(trade.order_uuid || "");
    if (!orderUuid) continue;
    const list = tradesByOrder.get(orderUuid) || [];
    list.push(trade);
    tradesByOrder.set(orderUuid, list);
  }

  const normalized: OrderLike[] = orders.map((order): OrderLike => {
    const orderTrades = tradesByOrder.get(String(order.uuid || "")) || [];
    let executedVolume = orderTrades.reduce((sum, trade) => sum + finite(trade.volume), 0);
    let executedFunds = orderTrades.reduce((sum, trade) => sum + finite(trade.funds), 0);
    let fee = orderTrades.reduce((sum, trade) => sum + finite(trade.fee), 0);

    if (executedVolume <= 0) executedVolume = finite(order.executed_volume ?? order.volume);
    if (executedFunds <= 0) executedFunds = finite(order.executed_funds_krw) || fallbackFunds(order, executedVolume);
    if (fee <= 0) fee = finite(order.paid_fee);

    const avgExecutedPrice = finite(order.avg_executed_price) || (
      executedVolume > 0 && executedFunds > 0 ? executedFunds / executedVolume : 0
    );

    return {
      ...order,
      executed_volume: executedVolume,
      executed_funds_krw: executedFunds,
      avg_executed_price: avgExecutedPrice || null,
      paid_fee: fee,
      total_cost_krw: order.side === "bid" ? executedFunds + fee : null,
      net_proceeds_krw: order.side === "ask" ? executedFunds - fee : null,
    };
  });

  // FIFO fallback for servers that do not yet expose per-exit realized PnL.
  // If the trade window does not contain enough buy history, leave PnL unknown.
  const lotsByMarket = new Map<string, Lot[]>();
  const pnlByOrder = new Map<string, number>();
  const chronologicalTrades = [...trades].sort((a, b) => {
    const at = Date.parse(String(a.created_at || ""));
    const bt = Date.parse(String(b.created_at || ""));
    return (Number.isFinite(at) ? at : 0) - (Number.isFinite(bt) ? bt : 0);
  });

  for (const trade of chronologicalTrades) {
    const market = String(trade.market || "");
    const side = String(trade.side || "");
    const orderUuid = String(trade.order_uuid || "");
    const quantity = finite(trade.volume);
    const funds = finite(trade.funds);
    const fee = finite(trade.fee);
    if (!market || quantity <= 0) continue;

    const lots = lotsByMarket.get(market) || [];
    lotsByMarket.set(market, lots);

    if (side === "bid") {
      lots.push({ quantity, costPerUnit: (funds + fee) / quantity });
      continue;
    }
    if (side !== "ask") continue;

    let remaining = quantity;
    let costBasis = 0;
    for (const lot of lots) {
      if (remaining <= 1e-12) break;
      if (lot.quantity <= 1e-12) continue;
      const used = Math.min(lot.quantity, remaining);
      costBasis += used * lot.costPerUnit;
      lot.quantity -= used;
      remaining -= used;
    }
    if (remaining <= 1e-8 && orderUuid) {
      pnlByOrder.set(orderUuid, (funds - fee) - costBasis);
    }
  }

  return normalized.map((order): OrderLike => ({
    ...order,
    realized_pnl_krw: order.realized_pnl_krw ?? pnlByOrder.get(String(order.uuid || "")) ?? null,
  }));
}
